import { Inter_400Regular } from "@expo-google-fonts/inter/400Regular";
import { Inter_500Medium } from "@expo-google-fonts/inter/500Medium";
import { Inter_600SemiBold } from "@expo-google-fonts/inter/600SemiBold";
import { Inter_700Bold } from "@expo-google-fonts/inter/700Bold";
import * as Sentry from "@sentry/react-native";
import { useFonts } from "expo-font";
import { Stack } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { AppLifecycle, useStartupUpdateGate } from "~/lib/app-updates";
import { BackendStatusOverlay } from "~/components/BackendStatusOverlay";
import { useEffect } from "react";
import { StatusBar } from "expo-status-bar";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { QuerySessionBoundary } from "~/components/QuerySessionBoundary";
import { MfaGate } from "~/components/composed/MfaGate";
import { LocalDataFrame } from "~/components/composed/LocalDataBanner";
import { WebAccessGate } from "~/components/WebAccessGate";
import { AuthProvider } from "~/hooks/useAuth";
import { CountersProvider } from "~/state/counters";
import { queryClient } from "~/lib/query-client";
import { persistOptions } from "~/lib/query-persist";
import { ThemeProvider, useTheme } from "~/styles/theme";

void SplashScreen.preventAutoHideAsync();

function RootLayout() {
  const [fontsLoaded, fontError] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
  });

  // Hold the splash until fonts load and the launch-time update check finishes (4s cap).
  const updateCheckDone = useStartupUpdateGate();
  const ready = (fontsLoaded || Boolean(fontError)) && updateCheckDone;

  useEffect(() => {
    if (ready) void SplashScreen.hideAsync();
  }, [ready]);

  if (!ready) return null;

  return (
    <ThemeProvider>
      <AppLifecycle />
      <BackendStatusOverlay />
      <ThemedApp />
    </ThemeProvider>
  );
}

function ThemedApp() {
  const { mode, colors } = useTheme();
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      <AuthProvider>
        <MfaGate>
          <QuerySessionBoundary>
            <StatusBar style={mode === "dark" ? "light" : "dark"} />
            <WebAccessGate>
              <CountersProvider>
                <LocalDataFrame>
                  <Stack
                    screenOptions={{
                      headerShown: false,
                      contentStyle: { backgroundColor: colors.background },
                    }}
                  >
                    <Stack.Screen name="index" />
                    <Stack.Screen
                      name="counter/new"
                      options={{ presentation: "modal" }}
                    />
                    <Stack.Screen
                      name="counter/[id]"
                      options={{ presentation: "modal" }}
                    />
                    <Stack.Screen
                      name="account"
                      options={{ presentation: "modal" }}
                    />
                    <Stack.Screen
                      name="support/index"
                      options={{ presentation: "modal" }}
                    />
                    <Stack.Screen
                      name="support/[id]"
                      options={{ presentation: "modal" }}
                    />
                  </Stack>
                </LocalDataFrame>
              </CountersProvider>
            </WebAccessGate>
          </QuerySessionBoundary>
        </MfaGate>
      </AuthProvider>
    </PersistQueryClientProvider>
  );
}

export default Sentry.wrap(RootLayout);
